import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import { makeStyles } from '@material-ui/core/styles';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import { useFormik } from 'formik';
import { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import * as Yup from 'yup';

import { DRAWER_TABS, ROUTES } from '../../constants';
import { Dialog, Footer, Loader } from '../../shared/components';
import { User } from '../../shared/types';
import {
  AdminTable,
  ContactSupport,
  Drawer,
  EditForm,
  RemoveUserDialog,
} from './components';

type ViewProps = {
  isLoading: boolean;
  patients: Array<User> | undefined;
  admins: Array<User> | undefined;
  careProviders: Array<User> | undefined;
  removeUser: (userId: string, selectedDrawerTab: DRAWER_TABS) => void;
  editUser: (user: User, selectedDrawerTab: DRAWER_TABS) => void;
  inviteUser: (email: string, selectedDrawerTab: DRAWER_TABS) => void;
};

type DialogProps = {
  open: boolean;
  selectedDrawerTab: DRAWER_TABS;
  userToEdit?: User;
  closeDialog: () => void;
  openRemoveUserDialog?: () => void;
  editUser?: (user: User, selectedDrawerTab: DRAWER_TABS) => void;
  inviteUser?: (email: string, selectedDrawerTab: DRAWER_TABS) => void;
};

type paramsType = {
  usersType: DRAWER_TABS | undefined;
};

const useStyles = makeStyles((theme) => ({
  drawerWrapper: {
    minHeight: '100vh',
    paddingBottom: '250px',
  },
  content: {
    margin: 'auto',
    padding: theme.spacing(10, 3),
    ...theme.mixins.toolbar,
    backgroundColor: '#F2F2F2',
  },
  contentHeader: {
    fontWeight: 600,
    fontSize: '18px',
    lineHeight: '21.09px',
    letterSpacing: '0.1px',
  },
  inviteNew: {
    color: theme.palette.primary.main,
    fontWeight: 600,
    fontSize: '16px',
    lineHeight: '18.75px',
    cursor: 'pointer',
  },
  addUserForm: {
    marginTop: '15px',
  },
  dialogTextField: {
    marginBottom: '40px',
    width: '55%',
    marginLeft: '12%',
    '& label': {
      fontSize: '13px',
    },
    '& input': {
      color: theme.palette.text.secondary,
    },
  },
  button: {
    '&:disabled': {
      backgroundColor: '#92C6C1',
      color: 'white',
    },
    width: '140px',
  },
  inviteNewButton: {
    width: '152px',
    height: '40px',
    fontSize: '15px',
  },
  inviteNewLable: {},
  actionWrapper: {
    display: 'flex',
    justifyContent: 'space-between',
  },
}));

const EditUserDialog: React.FC<DialogProps> = ({
  open,
  userToEdit,
  closeDialog,
  selectedDrawerTab,
  openRemoveUserDialog,
  editUser,
}) => {
  const dialogTitle = () => {
    if (selectedDrawerTab === DRAWER_TABS.PATIENTS) {
      return `Edit patient information`;
    }
    if (selectedDrawerTab === DRAWER_TABS.ADMINS) {
      return `Edit admin information`;
    }
    return `Edit care provider information`;
  };

  return (
    <>
      {userToEdit && editUser && (
        <Dialog open={open} title={dialogTitle()} closeDialog={closeDialog}>
          <EditForm
            selectedDrawerTab={selectedDrawerTab}
            closeDialog={closeDialog}
            userToEdit={userToEdit}
            openRemoveUserDialog={openRemoveUserDialog}
            editUser={(user) => editUser(user, selectedDrawerTab)}
          />
        </Dialog>
      )}
    </>
  );
};

const InviteUserDialog: React.FC<DialogProps> = ({
  open,
  closeDialog,
  inviteUser,
  selectedDrawerTab,
}) => {
  const classes = useStyles();
  const [isFormValid, setIsFormValid] = useState<boolean>(false);
  const initialValues = {
    email: '',
  };

  const validationSchema = Yup.object().shape({
    email: Yup.string().email('Invalid email address').required('Required'),
  });

  const dialogTitle = () => {
    if (selectedDrawerTab === DRAWER_TABS.PATIENTS) {
      return `Send invite to a new patient`;
    }
    if (selectedDrawerTab === DRAWER_TABS.ADMINS) {
      return `Send invite to a new admin`;
    }

    return `Send invite to a new care practitioner`;
  };

  const onSubmit = () => {
    if (inviteUser) {
      inviteUser(values.email, selectedDrawerTab);
      closeDialog();
    }
  };

  const { errors, values, handleSubmit, handleChange, isValid } = useFormik({
    initialValues,
    validationSchema,
    onSubmit,
  });

  useEffect(() => {
    if (values !== initialValues) {
      setIsFormValid(isValid);
    }
  }, [isValid, values]);

  return (
    <Dialog open={open} title={dialogTitle()} closeDialog={closeDialog}>
      <form onSubmit={handleSubmit} className={classes.addUserForm}>
        <TextField
          className={classes.dialogTextField}
          id="email"
          name="email"
          label="Email"
          type="email"
          helperText={errors.email}
          error={Boolean(errors.email)}
          variant="outlined"
          onChange={handleChange}
          value={values.email}
        />
        <div className={classes.actionWrapper}>
          <Button
            className={classes.button}
            onClick={closeDialog}
            color="primary"
            variant="outlined">
            Cancel
          </Button>
          <Button
            className={classes.button}
            disabled={!isFormValid}
            color="primary"
            variant="contained"
            type="submit">
            Send
          </Button>
        </div>
      </form>
    </Dialog>
  );
};

const View: React.FC<ViewProps> = ({
  isLoading,
  patients,
  admins,
  careProviders,
  removeUser,
  editUser,
  inviteUser,
}) => {
  const classes = useStyles();
  const history = useHistory();

  const { usersType } = useParams<paramsType>();
  const [selectedDrawerTab, setSelectedDrawerTab] = useState<DRAWER_TABS>(
    usersType || DRAWER_TABS.PATIENTS
  );

  const [openEditDialog, setOpenEditDialog] = useState<boolean>(false);
  const [openInviteUserDialog, setOpenInviteUserDialog] = useState<boolean>(
    false
  );
  const [openRemoveUserDialog, setOpenRemoveUserDialog] = useState<boolean>(
    false
  );

  const [userToEdit, setUserToEdit] = useState<User | undefined>();

  const handelEditDialog = (user: User) => {
    setUserToEdit(user);
    setOpenEditDialog(true);
  };

  const usersToRender = () => {
    if (selectedDrawerTab === DRAWER_TABS.PATIENTS) {
      return sortUsersByName(patients);
    }
    if (selectedDrawerTab === DRAWER_TABS.CARE_PROVIDERS) {
      return sortUsersByName(careProviders);
    }
    return sortUsersByName(admins);
  };

  const sortUsersByName = (users: User[] | undefined) => {
    return users?.sort((a, b) => a.name.localeCompare(b.name));
  };

  const handleOpenRemoveUserDialog = () => {
    setOpenEditDialog(false);
    if (selectedDrawerTab === DRAWER_TABS.PATIENTS) {
      const supportEmail = 'support@halterix.com';
      window.location.href = `mailto:${supportEmail}`;
    } else {
      setOpenRemoveUserDialog(true);
    }
  };

  const handleCloseRemoveUserDailog = () => {
    setOpenRemoveUserDialog(false);
    if (userToEdit && userToEdit.id) {
      removeUser(userToEdit.id, selectedDrawerTab);
    }
  };

  useEffect(() => {
    history.push(`${ROUTES.ADMIN_PANEL}/${selectedDrawerTab}`);
  }, [selectedDrawerTab]);

  if (isLoading) {
    return <Loader />;
  }

  return (
    <>
      <div className={classes.drawerWrapper}>
        <Drawer
          selectedTab={selectedDrawerTab}
          setSelectedTab={setSelectedDrawerTab}>
          <Grid className={classes.content} container spacing={4}>
            <Grid item xs>
              <Typography className={classes.contentHeader}>
                {selectedDrawerTab}
              </Typography>
            </Grid>
            <Grid item>
              <Button
                color="primary"
                variant="contained"
                className={classes.inviteNewButton}
                onClick={() => setOpenInviteUserDialog(true)}>
                Invite New +
              </Button>
            </Grid>
            <Grid item xs={12}>
              <AdminTable
                users={usersToRender()}
                selectedDrawerTab={selectedDrawerTab}
                handelEditDialog={handelEditDialog}
              />
            </Grid>
            {selectedDrawerTab === DRAWER_TABS.ADMINS && <ContactSupport />}
          </Grid>
        </Drawer>
        {/* dialogs */}
        {openEditDialog && (
          <EditUserDialog
            open={openEditDialog}
            userToEdit={userToEdit}
            closeDialog={() => setOpenEditDialog(false)}
            selectedDrawerTab={selectedDrawerTab}
            openRemoveUserDialog={handleOpenRemoveUserDialog}
            editUser={(user) => editUser(user, selectedDrawerTab)}
          />
        )}
        {openInviteUserDialog && (
          <InviteUserDialog
            open={openInviteUserDialog}
            closeDialog={() => setOpenInviteUserDialog(false)}
            selectedDrawerTab={selectedDrawerTab}
            inviteUser={inviteUser}
          />
        )}
        {userToEdit && openRemoveUserDialog && (
          <RemoveUserDialog
            selectedDrawerTab={selectedDrawerTab}
            open={openRemoveUserDialog}
            removeUser={handleCloseRemoveUserDailog}
            closeDialog={() => setOpenRemoveUserDialog(false)}
          />
        )}
      </div>
      <Footer />
    </>
  );
};

export default View;
